/* @flow */
import * as React from "react";
import styled, { css } from "styled-components";
import Media from "react-media";
import Gettext from "../common/Gettext";
import candleArrow from "../../images/candle-arrow.svg";
import * as Helpers from "../Chart/NextGenChartMath";

const InfoSvg = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="2294.453 672.95 16 16"
    width="16"
    height="16"
    fill="currentColor"
  >
    <path d="M2302.508 672.95a8.056 8.056 0 1 0 8.055 8.056 8.059 8.059 0 0 0-8.055-8.056zm-1 4.04h2.009v1.974h-2.01v-1.974zm2.981 7.97h-3.989v-1h1v-2.952h-1v-1h3.011v3.954h.972v1z" />
  </svg>
);

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 20000;
  overflow: auto;
`;

const PopupTable = styled.table`
  table-layout: fixed;
  width: 100%;
  height: 100%;
  margin: 0 auto;
`;

const Popup = styled.div`
  position: relative;
  max-width: 100%;
  width: 100%;
  min-height: 100%;
  height: 100%;
  margin: 0 auto;
  text-align: center;
  overflow: hidden;
  background-color: ${props => props.theme.popupBackground};
`;

const Close = styled.div`
  position: absolute;
  top: ${props => (props.desktop ? "9px" : "12px")};
  right: 12px;
  width: 20px;
  height: 20px;
  color: #96949b;
  transition: color 0.15s ease;
  cursor: pointer;

  :hover {
    color: #60a31c;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: calc(100% + 30px);
  min-height: 45px;
  margin-left: -15px;
  margin-right: -15px;
  padding-left: 15px;
  padding-right: 40px;
  background-color: ${props => props.theme.popupDarkBackground};
  border-bottom: 1px solid ${props => props.theme.mainSeparator};
`;

const HeaderTitle = styled.div`
  font-weight: 400;
  font-size: 16px;
  color: ${({ theme }) => theme.foreground};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100%;
  padding: 0 15px 10px 15px;
`;

const CandleModal = styled.div`
  position: absolute;
  left: 50%;
  bottom: 100%;
  width: 312px;
  margin-bottom: 10px;
  padding: 10px 12px;
  transform: translateX(-50%);
  background-color: ${props => props.theme.popupBackground};
  box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.16);
  z-index: 5;
`;

const CandleTitleContainer = styled.div`
  margin-bottom: 8px;
  padding-left: 8px;
  padding-right: 25px;
  padding-bottom: 10px;
  text-align: left;
  border-bottom: 1px solid ${props => props.theme.mainSeparator};

  @media (max-width: 959px) {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    min-height: 38px;
    padding-bottom: 0;
  }
`;

const CandleTitle = styled.span`
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "14px"};
  line-height: 1.2;
  color: #feac00;
`;

const CandleText = styled.span`
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "12px"};
  line-height: 1.2;
  color: ${({ theme }) => theme.secondaryTextColor};
`;

const CandleItems = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  width: 288px;
  height: ${props => (props.short ? "76px" : "228px")};
  margin-bottom: 5px;
  margin-left: auto;
  margin-right: auto;

  @media (min-width: 420px) {
    width: 388px;
    height: ${props => (props.short ? "64px" : "190px")};
  }

  @media (min-width: 520px) {
    width: 488px;
    height: 152px;
    height: ${props => (props.short ? "51px" : "152px")};
  }

  @media (min-width: 960px) {
    width: 288px;
    height: 228px;
    height: ${props => (props.short ? "76px" : "228px")};
  }
`;

const CandleItem = styled.button`
  min-width: 88px;
  width: 88px;
  height: 30px;
  margin-right: 12px;
  margin-bottom: 8px;
  padding: 0;
  padding-left: 12px;
  padding-right: 26px;
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "12px"};
  color: #99959d;
  text-align: left;
  text-transform: lowercase;
  background-color: ${props => props.theme.popupDarkBackground};
  border: none;
  cursor: pointer;

  :hover {
    color: #feac00;
  }

  ${props =>
    props.active &&
    css`
      color: ${({ theme }) => theme.foreground};
      background-image: url(${candleArrow});
      background-repeat: no-repeat;
      background-position: 62px 50%;
    `};
`;

const CandleInfo = styled.div`
  display: flex;
  justify-content: space-between;
  margin-left: auto;
  margin-right: auto;
  color: ${({ theme }) => theme.secondaryTextColor};
  text-align: left;

  @media (min-width: 420px) {
    width: 388px;
  }

  @media (min-width: 520px) {
    width: 488px;
  }

  @media (min-width: 960px) {
    width: auto;
  }
`;

const InfoIcon = styled.div`
  flex-basis: 17px;
  width: 17px;
  min-width: 17px;
  height: 17px;
  margin-top: -2px;
  margin-right: 7px;
  line-height: 0;

  > svg {
    flex-basis: 17px;
    width: 17px;
    min-width: 17px;
    height: 17px;
  }
`;

const InfoText = styled.div`
  width: calc(100% - 26px);
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "11px"};
  line-height: 18px;

  > p {
    max-width: 500px;
  }
`;

/* prettier-ignore */
const candleIntervals = {
  "5s": <React.Fragment>5&nbsp;<Gettext>sec</Gettext></React.Fragment>,
  "10s": <React.Fragment>10&nbsp;<Gettext>sec</Gettext></React.Fragment>,
  "15s": <React.Fragment>15&nbsp;<Gettext>sec</Gettext></React.Fragment>,
  "30s": <React.Fragment>30&nbsp;<Gettext>sec</Gettext></React.Fragment>,
  "1m": <React.Fragment>1&nbsp;<Gettext>min</Gettext></React.Fragment>,
  "2m": <React.Fragment>2&nbsp;<Gettext>min</Gettext></React.Fragment>,
  "5m": <React.Fragment>5&nbsp;<Gettext>min</Gettext></React.Fragment>,
  "10m": <React.Fragment>10&nbsp;<Gettext>min</Gettext></React.Fragment>,
  "15m": <React.Fragment>15&nbsp;<Gettext>min</Gettext></React.Fragment>,
  "30m": <React.Fragment>30&nbsp;<Gettext>min</Gettext></React.Fragment>,
  "1h": <React.Fragment>1&nbsp;<Gettext>hour</Gettext></React.Fragment>,
  "2h": <React.Fragment>2&nbsp;<Gettext>hour</Gettext></React.Fragment>,
  "4h": <React.Fragment>4&nbsp;<Gettext>hours</Gettext></React.Fragment>,
  "8h": <React.Fragment>8&nbsp;<Gettext>hours</Gettext></React.Fragment>,
  "12h": <React.Fragment>12&nbsp;<Gettext>hours</Gettext></React.Fragment>,
  "1D": <React.Fragment>1&nbsp;<Gettext>Day</Gettext></React.Fragment>,
  "1W": <React.Fragment>1&nbsp;<Gettext>week</Gettext></React.Fragment>,
  "1M": <React.Fragment>1&nbsp;<Gettext>Month</Gettext></React.Fragment>
};

export function HumanInterval(props: { children: string }) {
  return candleIntervals[props.children] || props.children;
}

export const normalHistoryIntervals = Object.keys(candleIntervals);
export const isShortHistoryIntervals = normalHistoryIntervals.slice(0, 6);

type Props = {
  value: number,
  onChange: number => void
};

function DesktopModal(props) {
  const intervals = Helpers.isShortHistoryStock(props.stock)
    ? isShortHistoryIntervals
    : normalHistoryIntervals;

  return (
    <CandleModal>
      <CandleTitleContainer>
        <CandleTitle>
          <Gettext>Time interval</Gettext>
        </CandleTitle>{" "}
        <CandleText>
          <Gettext>only for bars & candles</Gettext>
        </CandleText>
        <Close onClick={props.onToggle} desktop={true}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21.414 21.414">
            <g>
              <line stroke="currentColor" strokeWidth="2" x2="20" y2="20" />
              <line stroke="currentColor" strokeWidth="2" x1="20" y2="20" />
            </g>
          </svg>
        </Close>
      </CandleTitleContainer>
      <CandleItems short={Helpers.isShortHistoryStock(props.stock)}>
        {intervals.map(item => (
          <CandleItem
            key={item}
            active={item === Helpers.stringifyTime(props.value)}
            onClick={() => props.onChange(Helpers.parseTime(item))}
          >
            <HumanInterval>{item}</HumanInterval>
          </CandleItem>
        ))}
      </CandleItems>
      <CandleInfo>
        <InfoIcon>
          <InfoSvg />
        </InfoIcon>
        <InfoText>
          <p>
            <Gettext>
              The top and bottom of each bar/candle indicate the highest and
              lowest prices of an instrument for a specified period of time. The
              opening and closing prices are displayed on the left and right
              side of a bar.
            </Gettext>
          </p>
        </InfoText>
      </CandleInfo>
    </CandleModal>
  );
}

function MobileModal(props) {
  const intervals = Helpers.isShortHistoryStock(props.stock)
    ? isShortHistoryIntervals
    : normalHistoryIntervals;

  return (
    <Overlay>
      <PopupTable>
        <tbody>
          <tr>
            <td style={{ verticalAlign: "middle" }}>
              <Popup>
                <Content>
                  <div>
                    <Close onClick={props.onToggle}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 21.414 21.414"
                      >
                        <g>
                          <line
                            stroke="currentColor"
                            strokeWidth="2"
                            x2="20"
                            y2="20"
                          />
                          <line
                            stroke="currentColor"
                            strokeWidth="2"
                            x1="20"
                            y2="20"
                          />
                        </g>
                      </svg>
                    </Close>
                    <Header>
                      <HeaderTitle>
                        <Gettext>Interval selection</Gettext>
                      </HeaderTitle>
                    </Header>
                    <CandleTitleContainer>
                      <div>
                        <CandleTitle>
                          <Gettext>Time interval</Gettext>
                        </CandleTitle>{" "}
                        <CandleText>
                          <Gettext>only for bars & candles</Gettext>
                        </CandleText>
                      </div>
                    </CandleTitleContainer>
                    <CandleItems
                      short={Helpers.isShortHistoryStock(props.stock)}
                    >
                      {intervals.map(item => (
                        <CandleItem
                          key={item}
                          active={item === Helpers.stringifyTime(props.value)}
                          onClick={() =>
                            props.onChange(Helpers.parseTime(item))
                          }
                        >
                          <HumanInterval>{item}</HumanInterval>
                        </CandleItem>
                      ))}
                    </CandleItems>
                  </div>
                  <CandleInfo>
                    <InfoIcon>
                      <InfoSvg />
                    </InfoIcon>
                    <InfoText>
                      <p>
                        <Gettext>
                          The top and bottom of each bar/candle indicate the
                          highest and lowest prices of an instrument for a
                          specified period of time. The opening and closing
                          prices are displayed on the left and right side of a
                          bar.
                        </Gettext>
                      </p>
                    </InfoText>
                  </CandleInfo>
                </Content>
              </Popup>
            </td>
          </tr>
        </tbody>
      </PopupTable>
    </Overlay>
  );
}

function IntervalsModal(props: Props) {
  return (
    <Media query="(min-width: 959px)">
      {matches =>
        matches ? <DesktopModal {...props} /> : <MobileModal {...props} />
      }
    </Media>
  );
}

export default IntervalsModal;



// WEBPACK FOOTER //
// ./src/components/TradeModals/IntervalsModal.js